import React from 'react';
import { ErrorBoundary as ReactErrorBoundary } from 'react-error-boundary';
import { AlertCircle, RefreshCcw } from 'lucide-react';
import { ERROR_MESSAGES } from './constants';
import { showToast } from './toast';

interface FallbackProps {
  error: Error;
  resetErrorBoundary: () => void;
}

function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const isNetworkError =
    error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('fetch');

  React.useEffect(() => {
    // Log error to monitoring service
    console.error('Application error:', error);

    // Show toast notification
    showToast.error({
      title: 'An error occurred',
      description: error.message,
    });
  }, [error]);

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-dark-bg">
      <div className="w-full max-w-md p-6 bg-dark-card rounded-lg border border-dark-border shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <AlertCircle className="h-6 w-6 text-red-400" />
          <h2 className="text-lg font-semibold text-dark-text">
            {isNetworkError ? ERROR_MESSAGES.NETWORK : ERROR_MESSAGES.GENERIC}
          </h2>
        </div>

        <div className="bg-red-500/10 rounded-md p-4 mb-6">
          <pre className="text-sm text-dark-muted whitespace-pre-wrap break-words">
            {error.message}
          </pre>
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={resetErrorBoundary}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-dark-text rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCcw className="h-4 w-4" />
            Try again
          </button>

          <a href="/" className="text-primary-400 hover:text-primary-300 transition-colors">
            Return home
          </a>
        </div>
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  onReset?: () => void;
  fallback?: React.ReactNode;
}

export function ErrorBoundary({ children, onReset, fallback }: ErrorBoundaryProps) {
  return (
    <ReactErrorBoundary
      FallbackComponent={fallback ? () => <>{fallback}</> : ErrorFallback}
      onReset={onReset}
      onError={error => {
        console.error('Application error:', error);
        // Here you could send the error to your error tracking service
      }}
    >
      {children}
    </ReactErrorBoundary>
  );
}
